import { useCallback, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useLocation } from "react-router-dom";
import { RootState } from "../state/store";
import { setLoadingState } from "../state/appSlice";
import { saveFunctionRegistry } from "../state/configurationSlice";
import { useActions } from "./useActions";
import EncompassService from "../services/encompassService";
import { hasPluginConfigChanges } from "../utils/pluginConfigChangeDetector";
import { hasXPathConfigChanges } from "../utils/xpathConfigChangeDetector";
import { hasPushbackConfigChanges } from "../utils/pushbackConfigChangeDetector";
import { hasDocumentBundlingChanges, hasEventBundlingChanges } from "../utils/bundlingConfigChangeDetector";
import { getConfigPathFlags } from "../utils/pathMatcher";

interface UseSaveAndContinueOptions {
    navigateToHome: () => void;
    setUnsavedDialogOpen: (open: boolean) => void;
    onError?: (message: string) => void;
}

export const useSaveAndContinue = ({
    navigateToHome,
    setUnsavedDialogOpen,
    onError
}: UseSaveAndContinueOptions) => {
    const dispatch = useDispatch();
    const store = useStore<RootState>();
    const location = useLocation();
    const [savingAndContinue, setSavingAndContinue] = useState(false);
    const { isXPathMappingsPath, isConfigPushbackPath, isConfigBundlingPath } =
        getConfigPathFlags(location.pathname);

    const currentSaveFunctionId = useSelector((state: RootState) => state.configuration.currentSaveFunctionId);
    const currentSaveFunction = currentSaveFunctionId ? saveFunctionRegistry.getCurrentFunction() : null;

    const appState = useSelector((state: RootState) => state.appSlice);
    const configData = useSelector((state: RootState) => state.configPlugInData.data?.Configuration);
    const persistedConfig = useSelector((state: RootState) => state.configPlugInData.persistedData?.Configuration);
    const pluginConfiguration = useSelector((state: RootState) => state.configPlugInData.data);
    const xPathData = useSelector((state: RootState) => state.xPathData.data);
    const persistedXPathData = useSelector((state: RootState) => state.xPathData.persistedData);
    const pushbackData = useSelector((state: RootState) => state.configPushbackDefaults.data);
    const persistedPushbackData = useSelector((state: RootState) => state.configPushbackDefaults.persistedData);
    const documentBundles = useSelector((state: RootState) => state.configDocumentBundles.data);
    const persistedDocumentBundles = useSelector((state: RootState) => state.configDocumentBundles.persistedData);
    const eventBundles = useSelector((state: RootState) => state.configEventBundles.data);
    const persistedEventBundles = useSelector((state: RootState) => state.configEventBundles.persistedData);

    const {
        saveConfigPlugInData,
        saveConfigXPathData,
        saveConfigPushbackData,
        saveConfigBundleData,
        saveConfigBundleEventData
    } = useActions();

    const handleSaveAndContinue = useCallback(async () => {
        setUnsavedDialogOpen(false);
        setSavingAndContinue(true);
        dispatch(setLoadingState({ loading: true, loadingMessage: "Saving changes..." }));

        const cleanup = () => {
            dispatch(setLoadingState({ loading: false }));
            setSavingAndContinue(false);
        };

        let hasSaveError = false;
        const recordSaveError = (message: string, error: unknown) => {
            hasSaveError = true;
            console.warn(message, error);
            onError?.(message);
        };

        const resolvedConfigId = appState.configId || (EncompassService.getConfigId() as string);

        const hasPluginChanges = configData
            ? hasPluginConfigChanges(configData, persistedConfig)
            : false;
        if (hasPluginChanges && pluginConfiguration && resolvedConfigId) {
            try {
                await saveConfigPlugInData(resolvedConfigId, pluginConfiguration, appState.accessToken);
            } catch (error) {
                recordSaveError("Failed to save plugin configuration", error);
            }
        }

        if (!isXPathMappingsPath) {
            const fieldMappings = xPathData?.Configuration?.FieldMappings;
            const hasXPathChanges = xPathData
                ? hasXPathConfigChanges(xPathData, persistedXPathData)
                : false;
            if (fieldMappings && hasXPathChanges && resolvedConfigId) {
                try {
                    await saveConfigXPathData(resolvedConfigId, fieldMappings, appState.accessToken);
                } catch (error) {
                    recordSaveError("Failed to save XPath mappings", error);
                }
            }
        }

        if (!isConfigPushbackPath) {
            const pushbackConfig = pushbackData?.Configuration;
            const hasPushbackChanges = pushbackData
                ? hasPushbackConfigChanges(pushbackData, persistedPushbackData)
                : false;
            if (pushbackConfig && hasPushbackChanges && resolvedConfigId) {
                try {
                    await saveConfigPushbackData(resolvedConfigId, pushbackConfig, appState.accessToken);
                } catch (error) {
                    recordSaveError("Failed to save pushback configuration", error);
                }
            }
        }

        if (!isConfigBundlingPath) {
            const docBundlingConfig = documentBundles?.Configuration?.Bundling;
            const eventBundlingConfig = eventBundles?.Configuration?.Bundling;
            const hasDocumentBundlingChange = documentBundles
                ? hasDocumentBundlingChanges(documentBundles, persistedDocumentBundles)
                : false;
            const hasEventBundlingChange = eventBundles
                ? hasEventBundlingChanges(eventBundles, persistedEventBundles)
                : false;
            if (docBundlingConfig && hasDocumentBundlingChange && resolvedConfigId) {
                try {
                    await saveConfigBundleData(resolvedConfigId, docBundlingConfig, appState.accessToken);
                } catch (error) {
                    recordSaveError("Failed to save document bundling", error);
                }
            }
            if (eventBundlingConfig && hasEventBundlingChange && resolvedConfigId) {
                try {
                    await saveConfigBundleEventData(resolvedConfigId, eventBundlingConfig, appState.accessToken);
                } catch (error) {
                    recordSaveError("Failed to save event bundling", error);
                }
            }
        }

        if (currentSaveFunction) {
            try {
                await Promise.resolve(currentSaveFunction());
            } catch (error) {
                console.warn("Save function failed:", error);
                setUnsavedDialogOpen(true);
                onError?.("Failed to save changes");
                cleanup();
                return;
            }
        }

        const currentPageHasUnsavedChanges = (() => {
            const latestState = store.getState();
            if (isXPathMappingsPath) {
                const latestXPathData = latestState.xPathData.data;
                const latestPersistedXPathData = latestState.xPathData.persistedData;
                return latestXPathData
                    ? hasXPathConfigChanges(latestXPathData, latestPersistedXPathData)
                    : false;
            }
            if (isConfigPushbackPath) {
                const latestPushbackData = latestState.configPushbackDefaults.data;
                const latestPersistedPushbackData = latestState.configPushbackDefaults.persistedData;
                return latestPushbackData
                    ? hasPushbackConfigChanges(latestPushbackData, latestPersistedPushbackData)
                    : false;
            }
            if (isConfigBundlingPath) {
                const latestDocumentBundles = latestState.configDocumentBundles.data;
                const latestPersistedDocumentBundles = latestState.configDocumentBundles.persistedData;
                const latestEventBundles = latestState.configEventBundles.data;
                const latestPersistedEventBundles = latestState.configEventBundles.persistedData;
                const hasDocumentBundlingChange = latestDocumentBundles
                    ? hasDocumentBundlingChanges(latestDocumentBundles, latestPersistedDocumentBundles)
                    : false;
                const hasEventBundlingChange = latestEventBundles
                    ? hasEventBundlingChanges(latestEventBundles, latestPersistedEventBundles)
                    : false;
                return hasDocumentBundlingChange || hasEventBundlingChange;
            }
            return false;
        })();

        if (currentPageHasUnsavedChanges) {
            setUnsavedDialogOpen(true);
            onError?.("There are unsaved changes on this page. Please save or discard them before continuing.");
            cleanup();
            return;
        }

        if (hasSaveError) {
            setUnsavedDialogOpen(true);
            cleanup();
            return;
        }

        cleanup();
        navigateToHome();
    }, [
        appState.accessToken,
        appState.configId,
        configData,
        currentSaveFunction,
        dispatch,
        documentBundles,
        eventBundles,
        location.pathname,
        navigateToHome,
        onError,
        persistedConfig,
        persistedDocumentBundles,
        persistedEventBundles,
        persistedPushbackData,
        persistedXPathData,
        pluginConfiguration,
        pushbackData,
        saveConfigBundleData,
        saveConfigBundleEventData,
        saveConfigPlugInData,
        saveConfigPushbackData,
        saveConfigXPathData,
        setUnsavedDialogOpen,
        xPathData
    ]);

    return { handleSaveAndContinue, savingAndContinue };
};
